import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Icon,
  Stack,
  AppBar,
  Avatar,
  Select,
  Toolbar,
  MenuItem,
  IconButton,
  Typography,
  FormControl,
  useMediaQuery,
  Menu,
  Badge,
} from "@mui/material";
import useUserStore from "store/zustand/user";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import { getSvgByLanguage } from "../Flag/index";
// import { useThemeMode } from "../../context/ThemeModeContext";
import useLocalizationStore from "store/zustand/localization";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import { baseURL } from "store/services/base.config";
import { getInitials } from "sections/Admin/customers/CustomerTable";
import { getNotification, updateUser } from "store/services/user.service";
// import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
// import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";

function Header({ open, onToggleSidebar, drawerWidth, collapsedWidth, title }) {
  const theme = useTheme();
  const { user, setUserLogout } = useUserStore();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const { setLanguage, language } = useLocalizationStore();
  const [selectedLanguage, setSelectedLanguage] = useState(language || "en");
  const [userMenuEl, setUserMenuEl] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t, i18n } = useTranslation();

  const handleLanguageChange = async (lang) => {
    setSelectedLanguage(lang);
    setLanguage(lang);
    i18n.changeLanguage(lang);
    if (user?.id) {
      try {
        await updateUser({ id: user.id, language: lang });
      } catch (error) {
        console.error("Failed to persist language preference", error);
      }
    }
  };

  useEffect(() => {
    setSelectedLanguage(language || "en");
  }, [language]);

  const handleLogout = async () => {
    setUserLogout();
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuEl(null);
  };

  const getNotificationCount = async () => {
    try {
      const res = await getNotification();
      setCount(res?.data || 0);
    } catch (error) {
      console.log("error", error);
    }
  };
  useEffect(() => {
    if (user?.role?.role_type !== "super_admin") {
      getNotificationCount();
    }
  }, [user?.role?.role_type]);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      color="transparent"
      sx={{
        borderBottom: (muiTheme) => `1px solid ${muiTheme.palette.divider}`,
        backgroundColor: theme.palette.primary.main,
        borderRadius: "0px",
        color: "#fff",
        width: {
          md: `calc(100% - ${open ? drawerWidth : collapsedWidth}px)`,
          xs: "100%",
        },
        ...(isMobile
          ? {
              marginLeft: 0,
              marginRight: 0,
            }
          : {
              marginLeft:
                language === "en"
                  ? `${open ? drawerWidth : collapsedWidth}px`
                  : undefined,
              marginRight:
                language !== "en"
                  ? `${open ? drawerWidth : collapsedWidth}px`
                  : undefined,
            }),
      }}
    >
      <Toolbar
        sx={{
          minHeight: 72,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          justifyItems="center"
          sx={{ width: "100%" }}
        >
          <IconButton
            onClick={onToggleSidebar}
            edge="start"
            color="inherit"
            aria-label="toggle sidebar menu"
            sx={{
              mr: 1,
              display: { xs: "inline-flex", md: "none" },
              backgroundColor: "rgba(255,255,255,0.1)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            {title}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* <IconButton
            color="inherit"
            onClick={toggleMode}
            sx={{
              backgroundColor: controlBackground,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
            }}
            aria-label={t("header.themeToggle")}
          > 
            {mode === "light" ? (
              <DarkModeOutlinedIcon fontSize="small" />
            ) : (
              <LightModeOutlinedIcon fontSize="small" />
            )}
          </IconButton> */}
          {user?.role?.role_type !== "super_admin" && (
            <>
              <Badge badgeContent={count} color="info">
                <NotificationsNoneIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate("/alerts")}
                />
              </Badge>
              <Box color="white">|</Box>
            </>
          )}

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton size={isMobile ? "small" : "medium"} aria-label="flag">
              {/* <SearchIcon /> */}
              <Icon
                sx={{ width: "1.5rem", height: "1.5rem" }}
                aria-label="flag"
              >
                {getSvgByLanguage(selectedLanguage)}
              </Icon>
            </IconButton>

            <FormControl
              size="small"
              name="language"
              aria-label="language"
              sx={{
                border: "none", // No border for the FormControl
                outline: "none",
              }}
            >
              <Select
                value={selectedLanguage}
                aria-labelledby="language-select" // Correctly associates aria-labelledby
                onChange={(event) => handleLanguageChange(event.target.value)}
                MenuProps={{
                  disableScrollLock: true,
                }}
                sx={{
                  padding: 0,
                  minWidth: 0,
                  border: "none", // No border for the FormControl
                  outline: "none", // Remove outline
                  color: "white !important", // Set text color to white
                  "& .MuiSelect-select": {
                    minWidth: "0 !important", // Make sure select width is minimal
                    color: "white !important", // Set text color to white
                    border: "none", // Remove border from the select input
                  },
                  "& .MuiOutlinedInput-root": {
                    border: "none", // Remove border from the root input
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none", // Remove focus border
                    },
                  },
                  "& .MuiSelect-icon": {
                    color: "white !important", // Set the arrow icon color
                  },
                }}
              >
                <MenuItem value="en">EN</MenuItem>
                <MenuItem value="ar">AR</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            gap={1}
            alignItems="center"
            width={{ sm: "140px" }}
            sx={{ cursor: "pointer" }}
            onClick={handleUserMenuOpen}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "primary.light",
                color: "primary.main",
              }}
              src={`${baseURL}${user?.profile_image}`}
            >
              {getInitials(`${user?.first_name} ${user?.last_name}`)}
            </Avatar>
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                flexDirection: "column",
                width: "fit-content",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  whiteSpace: "nowrap", // stop wrapping
                  overflow: "hidden", // hide overflow
                  textOverflow: "ellipsis", // add "..."}}
                  width: "100px",
                }}
                noWrap
              >
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  textTransform: "capitalize",
                }}
              >
                {user?.role?.role_type === "super_admin"
                  ? t("nav.super")
                  : user?.role?.name}
              </Typography>
            </Box>
            {/* <IconButton color="inherit" size="small">
              <ExpandMoreIcon />
            </IconButton> */}
          </Stack>
          <Menu
            anchorEl={userMenuEl}
            open={Boolean(userMenuEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            keepMounted
          >
            <MenuItem
              onClick={() => {
                handleUserMenuClose();
                handleLogout();
              }}
            >
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              {t("Logout")}
            </MenuItem>
          </Menu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

Header.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggleSidebar: PropTypes.func.isRequired,
  drawerWidth: PropTypes.number.isRequired,
  collapsedWidth: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
};

export default Header;
